import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
  AiPackage,
  AiUsageResponse,
  BillingHistoryResponse,
  CancelSubscriptionDto,
  CancelSubscriptionResponse,
  ChangeTierDto,
  CreateSubscriptionDto,
  Subscription,
  SubscriptionTier,
  TrialStatusResponse,
  WithdrawalResponse,
} from '../../types/api/subscriptions';
import { subscriptionsApi } from '../../services/api/subscriptions.api';
import { extractApiErrorCode } from '../../services/card-gate/card-gate-controller';

interface SubscriptionState {
  subscription: Subscription | null;
  tiers: SubscriptionTier[];
  tiersB2c: SubscriptionTier[];
  tiersB2b: SubscriptionTier[];
  trial: TrialStatusResponse | null;
  billing: BillingHistoryResponse | null;
  aiUsage: AiUsageResponse | null;
  aiPackages: AiPackage[];
  loading: boolean;
  error: string | null;
  lastCancelResponse?: CancelSubscriptionResponse | null;
  lastWithdrawalResponse?: WithdrawalResponse | null;
}

const initialState: SubscriptionState = {
  subscription: null,
  tiers: [],
  tiersB2c: [],
  tiersB2b: [],
  trial: null,
  billing: null,
  aiUsage: null,
  aiPackages: [],
  loading: false,
  error: null,
  lastCancelResponse: null,
  lastWithdrawalResponse: null,
};

export const fetchSubscription = createAsyncThunk(
  'subscription/fetchSubscription',
  async (_, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.getCurrentSubscription();
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки подписки');
    }
  }
);

export const fetchTiers = createAsyncThunk(
  'subscription/fetchTiers',
  async (segment: 'b2c' | 'b2b' | undefined, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.getTiers(segment);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки тарифов');
    }
  }
);

export const fetchTrialStatus = createAsyncThunk(
  'subscription/fetchTrialStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.getTrialStatus();
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки trial статуса');
    }
  }
);

export const fetchBillingHistory = createAsyncThunk(
  'subscription/fetchBillingHistory',
  async ({ id, limit }: { id: string; limit?: number }, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.getBillingHistory(id, limit);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки биллинга');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async ({ id, dto }: { id: string; dto: CancelSubscriptionDto }, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.cancelSubscription(id, dto);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка отмены подписки');
    }
  }
);

export const requestWithdrawal = createAsyncThunk(
  'subscription/requestWithdrawal',
  async (id: string, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.requestWithdrawal(id);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка запроса возврата');
    }
  }
);

export const upgradeSubscription = createAsyncThunk(
  'subscription/upgradeSubscription',
  async ({ id, dto }: { id: string; dto: ChangeTierDto }, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.upgradeSubscription(id, dto);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка обновления тарифа');
    }
  }
);

export const downgradeSubscription = createAsyncThunk(
  'subscription/downgradeSubscription',
  async ({ id, dto }: { id: string; dto: ChangeTierDto }, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.downgradeSubscription(id, dto);
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка понижения тарифа');
    }
  }
);

/**
 * Отказ в покупке подписки — с ДОМЕННЫМ кодом, а не только текстом.
 *
 * Бэкенд отбивает покупку второй подписки при долге (M2.18) кодом
 * `SUBSCRIPTION_PAYMENT_PAST_DUE`, и по нему экран обязан увести человека на
 * «Обновить карту». Плоская строка `error.message` этот код теряла, и клиент
 * упирался в тупик ровно в тот момент, когда собирался заплатить.
 */
export interface SubscriptionPurchaseError {
  message: string;
  code?: string;
}

export const createNewSubscription = createAsyncThunk(
  'subscription/createNewSubscription',
  async (dto: CreateSubscriptionDto, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.createSubscription(dto);
    } catch (error: any) {
      const code = extractApiErrorCode(error);
      const payload: SubscriptionPurchaseError = {
        message: error?.message || 'Ошибка создания подписки',
        ...(code ? { code } : {}),
      };
      return rejectWithValue(payload);
    }
  }
);

export const fetchAiUsage = createAsyncThunk(
  'subscription/fetchAiUsage',
  async (_, { rejectWithValue }) => {
    try {
      return await subscriptionsApi.getAiUsage();
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки AI использования');
    }
  }
);

export const fetchAiPackages = createAsyncThunk(
  'subscription/fetchAiPackages',
  async (_, { rejectWithValue }) => {
    try {
      const list = await subscriptionsApi.getAiPackages();
      return Array.isArray(list) ? list : [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Ошибка загрузки AI пакетов');
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearSubscriptionError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload.subscription;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTiers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTiers.fulfilled, (state, action) => {
        state.loading = false;
        state.tiers = action.payload.tiers;
        const segment = action.meta.arg;
        if (segment === 'b2b') {
          state.tiersB2b = action.payload.tiers;
        } else {
          state.tiersB2c = action.payload.tiers;
        }
      })
      .addCase(fetchTiers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTrialStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrialStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.trial = action.payload;
      })
      .addCase(fetchTrialStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBillingHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.billing = action.payload;
      })
      .addCase(fetchBillingHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.lastCancelResponse = action.payload;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(requestWithdrawal.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestWithdrawal.fulfilled, (state, action) => {
        state.loading = false;
        state.lastWithdrawalResponse = action.payload;
      })
      .addCase(requestWithdrawal.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(upgradeSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upgradeSubscription.fulfilled, (state, action) => {
        state.loading = false;
        if (state.subscription) {
          state.subscription.tierId = action.payload.subscription.tierId;
          state.subscription.currentPeriodEnd = action.payload.subscription.currentPeriodEnd;
        }
      })
      .addCase(upgradeSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(downgradeSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downgradeSubscription.fulfilled, (state, action) => {
        state.loading = false;
        if (state.subscription) {
          state.subscription.tierId = action.payload.subscription.tierId;
        }
      })
      .addCase(downgradeSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createNewSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload.subscription as unknown as Subscription;
      })
      .addCase(createNewSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as SubscriptionPurchaseError | undefined)?.message ??
          null;
      })
      .addCase(fetchAiUsage.fulfilled, (state, action) => {
        state.aiUsage = action.payload as AiUsageResponse;
      })
      .addCase(fetchAiPackages.fulfilled, (state, action) => {
        state.aiPackages = action.payload as AiPackage[];
      });
  },
});

export const { clearSubscriptionError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
